import { gql } from '@apollo/client'
import { UserFragments } from '@/modules/users/fragments'
import { ActivitiesFragments } from './fragments'
import { graphql } from '@/graphql-setup'

export const GET_DRIVER_ACTIVITIES_AGGREGATED = graphql(`
  query DriverActivitiesAggregated(
    $userId: ID
    $dateStart: DateTime
    $dateEnd: DateTime
  ) {
    allDriverActivitiesAggregated(
      userId: $userId
      dateStart: $dateStart
      dateEnd: $dateEnd
    ) {
      timeWorkTotal
      timeWorkTotalForOvertime
      timeDriving
      timeOtherWork
      timeRest
      timePause
      timePaidRest
      timeAvailability
      timeOvertime
    }
  }
`)

export const GET_ALL_DRIVER_ACTIVITIES = graphql(`
  query GetAllDriverActivities(
    $userId: ID
    $dateStart: DateTime
    $dateEnd: DateTime
    $chart: Boolean = true
  ) {
    allFrontendDriverActivities(
      userId: $userId
      dateStart: $dateStart
      dateEnd: $dateEnd
      chart: $chart
    ) {
      id
      user {
        id
        username
        fullName
      }
      activityType
      datetimeStart
      datetimeEnd
      vehicleVin
      vehicleRegistrationNumber
      __typename
    }
  }
`)

export const GET_ISSUES = graphql(`
  query GetIssues($dateStart: DateTime, $dateEnd: DateTime, $userId: ID) {
    allIssues(dateStart: $dateStart, dateEnd: $dateEnd, userId: $userId) {
      edges {
        node {
          id
          severity
          timeOfIssueStart
          timeOfIssueEnd
        }
      }
    }
  }
`)

export const GET_SPECIFIC_CONDITIONS = graphql(`
  query GetSpecificConditions(
    $userId: ID
    $dateStart: DateTime
    $dateEnd: DateTime
    $first: Int
    $after: String
  ) {
    allSpecificConditions(
      userId: $userId
      dateStart: $dateStart
      dateEnd: $dateEnd
      first: $first
      after: $after
    ) {
      edges {
        node {
          id
          type
          timestamp
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`)

export const DRIVER_ACTIVITY_DAY_REPORT_QUERY = gql`
  query DriverActivityDayReport(
    $user: ID!
    $date: DateTime!
    $moduleConfig: String = "Activities"
  ) {
    driverActivityDayReport(user: $user, date: $date) {
      date
      regNr
      registrationNumbers

      groupedActivities {
        vehicleVin
        regNr

        startTime
        endTime

        timeWorkTotal
        timeDriving
        timeRest
        timePause
        timePaidRest
        timeAvailability
        timeOtherWork
        timeOvertime
      }

      diet {
        id
        salaryCode {
          id
          name
        }
        partOfDay
        comment
        isManuallyEdited
      }

      driverGrade {
        id
        grade
      }

      salaryDay {
        id
        date
        partOfDay
        salaryCode {
          id
          name
        }
        isManuallyEdited
      }

      sumDriverDiets
      sumSalaryDays

      lastFetched
      startTime
      dayStartLocation
      endTime
      dayEndLocation
      daylightSavingsChange {
        offset
        changeTime
        utcNewOffset
        utcOldOffset
      }

      timezoneData {
        timezone
        offset
      }

      timeWorkTotal
      timeDriving
      timeRest
      timePause
      timePaidRest
      timeOvertime
      timeAvailability
      timeOtherWork

      distance
      fuelUsage
      drivingGrade
      approved

      dataStatus

      drivenCoordinates {
        coordinates {
          lat
          lng
        }
        vehicleVin
      }

      user {
        id
        fullName
        salaryWorkTime(moduleConfigName: $moduleConfig) {
          id
          paidPauseThreshold
        }
      }
      activities {
        activityType
        datetimeStart
        datetimeEnd
        vehicleVin
        vehicleRegistrationNumber
      }
      issues {
        id
        createdAt
        timeOfIssueStart
      }
      groupedActivitySums {
        name
        id
        sum
      }
    }
    specificConditions(user: $user, date: $date) {
      id
      timestamp
      type
    }
  }
`

export const DRIVER_ACTIVITY_PERIOD_REPORT_QUERY = graphql(`
  query DriverActivityPeriodReport(
    $user: ID!
    $dateStart: DateTime!
    $dateEnd: DateTime!
    $useWorkDayReport: Boolean
  ) {
    report: driverActivityPeriodReport(
      user: $user
      dateStart: $dateStart
      dateEnd: $dateEnd
      useWorkDayReport: $useWorkDayReport
    ) {
      user {
        id
      }
      company {
        id
        onlyUseOwnedVehiclesInSalaryCalculation
      }
      groupedActivities {
        vehicleVin
        regNr

        salaryId
        salaryName

        startTime
        endTime

        timeWorkTotal
        timeDriving
        timeRest
        timePause
        timePaidRest
        timeAvailability
        timeOtherWork
        timeOvertime

        department {
          id
          name
          externalId
        }
      }
      unknownVehicleGroupedActivities {
        vehicleVin
        regNr

        date
        startTime
        endTime

        timeWorkTotal
        timeDriving
        timeOtherWork
        timeAvailability
      }
      startDate
      endDate
      timeWorkTotal
      timePaidRest
      timePause
      periodReports {
        dayReports {
          type
          date
          regNr
          registrationNumbers
          hasUnknownVehicle
          department {
            id
            name
            externalId
          }
          departments {
            id
            name
            externalId
          }
          groupedActivities {
            vehicleVin
            regNr

            salaryId
            salaryName

            startTime
            endTime

            timeWorkTotal
            timeDriving
            timeRest
            timePause
            timePaidRest
            timeAvailability
            timeOtherWork
            timeOvertime

            department {
              id
              name
              externalId
            }
          }

          addonsAsText

          diet {
            id
            salaryCode {
              id
              name
            }
            partOfDay
            comment
            isManuallyEdited
          }

          salaryDay {
            id
            date
            partOfDay
            salaryCode {
              id
              name
            }
            isManuallyEdited
          }

          comment {
            id
            comment
          }

          startTime
          endTime
          dayStartLocation
          dayEndLocation

          timeDriving
          timeOtherWork
          timeOvertime
          timeAvailability
          timeWorkTotal
          timeRest
          timePause
          timePaidRest
          dataStatus
          approved

          absence {
            reason
          }

          salaryIds
          salaryNames
        }
        periodStart
        periodEnd

        timeWorkTotal
        timeDriving
        timeOtherWork
        timeAvailability
        timeRest
        timePause
        timePaidRest

        dayOvertime
        periodOvertime
        overtime
        driverDiets {
          id
          salaryCode {
            id
            name
          }
          partOfDay
        }
        salaryDays {
          id
          date
          partOfDay
        }
      }
      timeDriving
      timeOtherWork
      timeAvailability

      dayOvertime
      periodOvertime
      overtime
      overtimeNextMonth

      sumDriverDietsDetailed {
        name
        sum
      }
      sumSalaryDaysDetailed {
        name
        sum
      }

      sumDriverDiets
      sumSalaryDays

      groupedActivitySums {
        name
        id
        sum
      }

      salaryWorkTime {
        useWeekOvertime
        useMonthOvertime
        paidPauseThreshold
      }
    }
    user(id: $user) {
      id
      fullName
    }
  }
`)

export const DRIVER_ACTIVITY_PERIOD_REPORT_SHALLOW_QUERY = graphql(`
  query DriverActivityPeriodReportShallow(
    $user: ID!
    $dateStart: DateTime!
    $dateEnd: DateTime!
    $weekPartitions: Boolean
    $useWorkDayReport: Boolean
  ) {
    report: driverActivityPeriodReport(
      user: $user
      dateStart: $dateStart
      dateEnd: $dateEnd
      weekPartitions: $weekPartitions
      useWorkDayReport: $useWorkDayReport
    ) {
      user {
        id
      }
      startDate
      endDate
      timeWorkTotal
      timePaidRest
      timeDriving
      timeOtherWork
      timeAvailability
      overtime

      sumDriverDietsDetailed {
        name
        sum
      }
      sumSalaryDaysDetailed {
        name
        sum
      }

      sumDriverDiets
      sumSalaryDays

      periodOvertime
      dayOvertime
      salaryWorkTime {
        paidPauseThreshold
        groupSalaryOnVehicle
      }

      groupedActivitySums {
        name
        id
        sum
      }
    }
    user(id: $user) {
      id
      fullName
    }
  }
`)

export const DRIVER_ACTIVITY_MONTH_SUMS_QUERY = gql`
  query DriverActivityPeriodSums($userId: ID!, $monthStart: DateTime!) {
    sums: driverActivityPeriodSums(userId: $userId, monthStart: $monthStart) {
      timeDriving
      timeOtherWork
      timeAvailability
      # TODO gql-error timePaidPause
      dayOvertime
    }
  }
`

export const ALL_ACTIVITIES_QUERY = gql`
  query AllActivitiesQuery(
    $user: ID
    $datetimeStart_Gte: DateTime
    $datetimeEnd_Lte: DateTime
  ) {
    allDriverActivities(
      user: $user
      datetimeStart_Gte: $datetimeStart_Gte
      datetimeEnd_Lte: $datetimeEnd_Lte
    ) {
      edges {
        node {
          ...DriverActivityInfo
        }
      }
    }
  }
  ${ActivitiesFragments.driverActivityInfo}
`

export const USER_WITH_ACTIVITIES_QUERY = gql`
  query UserWithActivities(
    $id: ID!
    $datetimeStart_Gte: DateTime
    $datetimeEnd_Lte: DateTime
  ) {
    user(id: $id) {
      ...UserInfo
      activities(
        datetimeStart_Gte: $datetimeStart_Gte
        datetimeEnd_Lte: $datetimeEnd_Lte
      ) {
        edges {
          node {
            ...DriverActivityInfo
          }
        }
      }
    }
  }
  ${UserFragments.info}
  ${ActivitiesFragments.driverActivityInfo}
`

export const ALL_DRIVER_FORECASTS_QUERY = graphql(`
  query AllForecastsQuery {
    allForecasts {
      user {
        id
        fullName
      }
      timestamp
      remainingDaily
      remainingWeekly
      remainingTwoWeekly
      remainingWeeklyWorkingTime
      totalWeeklyWorkingTime
      latestUpdate
    }
  }
`)

export const DRIVER_STATUS_QUERY = graphql(`
  query DriverStatusQuery(
    $q: String!
    $dateStart: Date = "2025-01-01"
    $dateEnd: Date = "2026-01-01"
    $mode: String
  ) {
    allDriverStatuses(
      q: $q
      dateStart: $dateStart
      dateEnd: $dateEnd
      mode: $mode
    ) {
      weekStatuses {
        weekStart
        weekEnd
        weekNumber
        dayStatuses {
          date
          isComplete
          source
        }
      }
      user {
        id
        fullName
      }
      startDate
      endDate
    }
  }
`)

export const VEHICLE_STATUS_QUERY = graphql(`
  query VehicleStatusQuery(
    $q: String!
    $dateStart: Date = "2025-01-01"
    $dateEnd: Date = "2026-01-01"
    $mode: String
  ) {
    allVehicleStatuses(
      q: $q
      dateStart: $dateStart
      dateEnd: $dateEnd
      mode: $mode
    ) {
      weekStatuses {
        weekStart
        weekEnd
        weekNumber
        dayStatuses {
          date
          isComplete
          source
        }
      }
      vehicle {
        id
        vin
        registrationNumber
      }
      startDate
      endDate
    }
  }
`)

export const VEHICLE_ACTIVITIES_WITHOUT_CARD = graphql(`
  query VehicleActivitiesWithoutCard(
    $datetimeStart: DateTime!
    $datetimeEnd: DateTime!
    $vehicleId: ID
    $previousDriverUserId: ID
    $nextDriverUserId: ID
    $sameDriverBeforeAndAfter: Boolean
    $explained: Boolean
  ) {
    vehicleActivitiesWithoutCard(
      datetimeStart: $datetimeStart
      datetimeEnd: $datetimeEnd
      vehicleId: $vehicleId
      previousDriverUserId: $previousDriverUserId
      nextDriverUserId: $nextDriverUserId
      sameDriverBeforeAndAfter: $sameDriverBeforeAndAfter
      explained: $explained
    ) {
      id
      start
      end
      duration
      vehicleVin
      vehicleRegistrationNumber
      explanation
      explained
      vehicle {
        id
        registrationNumber
        vin
      }
      nearbyVehicleExpirations {
        id
        description
        expirationDate
        expirationFrom
        expirationTo
        expirationType {
          id
          name
        }
        responsibleUser {
          id
          fullName
        }
      }
      activity {
        id
        vehicleVin
        datetimeStart
        datetimeEnd
        duration
      }
      previousDriverActivity {
        id
        datetimeStart
        datetimeEnd
        driverCardNumber
        user {
          id
          fullName
        }
      }
      nextDriverActivity {
        id
        datetimeStart
        datetimeEnd
        driverCardNumber
        user {
          id
          fullName
        }
      }
    }
  }
`)

export const DRIVER_LOCATIONS_FOR_VEHICLE_PERIOD = graphql(`
  query DriverLocationsForVehiclePeriod(
    $vehicleVin: String!
    $datetimeStart: DateTime!
    $datetimeEnd: DateTime!
  ) {
    driverLocationsForVehiclePeriod(
      vehicleVin: $vehicleVin
      datetimeStart: $datetimeStart
      datetimeEnd: $datetimeEnd
    ) {
      id
      vehicleVin
      latitude
      longitude
      datetimeStart
      speed
      user {
        id
        fullName
      }
    }
  }
`)

export const OUT_OF_SCOPE_DRIVING_EVENTS = graphql(`
  query OutOfScopeDrivingEvents(
    $datetimeStart: DateTime!
    $datetimeEnd: DateTime!
    $userId: ID
  ) {
    outOfScopeDrivingEvents(
      datetimeStart: $datetimeStart
      datetimeEnd: $datetimeEnd
      userId: $userId
    ) {
      id
      start
      end
      duration
      vehicleVin
      vehicleRegistrationNumber
      user {
        id
        fullName
      }
    }
  }
`)

export const TACHO_DRIVER_FILES_QUERY = graphql(`
  query AllTachoDriverFilesQuery(
    $first: Int = 250
    $after: String
    $q: String
    $processed: Boolean
  ) {
    allTachoDriverFiles(
      first: $first
      after: $after
      q: $q
      processed: $processed
    ) {
      edges {
        node {
          id
          file
          fileName
          driverCardNumber
          source
          user {
            id
            fullName
          }
          processed
          postProcessed
          createdAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export const TACHO_VEHICLE_FILES_QUERY = graphql(`
  query AllTachoVehicleFilesQuery(
    $first: Int = 250
    $after: String
    $q: String
    $processed: Boolean
  ) {
    allTachoVehicleFiles(
      first: $first
      after: $after
      q: $q
      processed: $processed
    ) {
      edges {
        node {
          id
          file
          fileName
          vehicleVin
          source
          processed
          postProcessed
          createdAt
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

export const TACHO_VEHICLE_FILE_QUERY = graphql(`
  query TachoVehicleFileQuery($id: ID!) {
    tachoVehicleFile(id: $id) {
      id
      fileName
      vehicleVin
      source
      processed
      postProcessed
      createdAt
      processedValue
    }
  }
`)

export const TACHO_DRIVER_FILE_QUERY = graphql(`
  query TachoDriverFileQuery($id: ID!) {
    tachoDriverFile(id: $id) {
      id
      fileName
      driverCardNumber
      source
      processed
      postProcessed
      createdAt
      processedValue
      user {
        id
        fullName
      }
    }
  }
`)

export const ALL_DRIVER_COMMENTS = graphql(`
  query AllDriverComments(
    $datetimeGt: DateTime
    $datetimeLt: DateTime
    $user: ID
  ) {
    allDriverComments(
      datetimeStart: $datetimeGt
      datetimeEnd: $datetimeLt
      userId: $user
    ) {
      id
      date
      comment
      user {
        id
        fullName
      }
    }
  }
`)

export const ALL_CARD_INSERT_PERIODS_START_STOP_COUNTRY = graphql(`
  query AllCardInsertPeriodsStartStopCountry(
    $userId: ID
    $dateStart: DateTime
    $dateEnd: DateTime
  ) {
    allCardInsertPeriods(
      userId: $userId
      dateStart: $dateStart
      dateEnd: $dateEnd
    ) {
      edges {
        node {
          id
          datetimeStart
          datetimeEnd
          startCountry
          stopCountry
          user {
            id
            fullName
          }
        }
      }
    }
  }
`)
